import { useState } from "react";
import { usePageTitle } from "../hooks/usePageTitle";
import FindUsSection from "../components/HomepageSection/FindUsSection";
import Footer from "../components/Footer";
import QuickActionModal from "../components/QuickActionModal";

// ─────────────────────────────────────────────
// Quick Actions data
// ─────────────────────────────────────────────
const ACTIONS = [
  {
    number: "01",
    key: "consultation",
    type: "Request",
    title: "Personalized Consultation",
    description:
      "Do you need proper and affordable coverage? Request a consultation now to have a personalized insurance plan that fits your specific needs.",
  },
  {
    number: "02",
    key: "coi",
    type: "Request",
    title: "Request Certificate of Insurance or Other Documents",
    description:
      "Request certificate of insurance below! One of our licensed agents will send it out as soon as possible.",
  },
  {
    number: "03",
    key: "policy-change",
    type: "Request",
    title: "Policy Change",
    description:
      "Do you need to make any changes to your existing policy? Submit your request below and we will update your policy accordingly.",
  },
  {
    number: "04",
    key: "contact-update",
    type: "Request",
    title: "Update Contact Info or Other Insured Items",
    description:
      "Request a policy update information on an existing policy below.",
  },
  {
    number: "05",
    key: "claim",
    type: "Report",
    title: "Report a Claim",
    description:
      "Report a claim below. One of our licensed agents will contact you shortly to provide personalized service throughout the claim process.",
  },
  {
    number: "06",
    key: "call-request",
    type: "Request",
    title: "Request a Call",
    description:
      "Do you need further assistance? Request a call below and one of our licensed agents will reach out to you promptly.",
  },
];

// ─────────────────────────────────────────────
// Contact info rows
// ─────────────────────────────────────────────
const CONTACT_ROWS = [
  {
    label: "Phone",
    value: "805-692-6900",
    href: "tel:8056926900",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.07 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3 1.18h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 8.91a16 16 0 0 0 5.65 5.65l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21 16.92z" />
      </svg>
    ),
  },
  {
    label: "Fax",
    value: "805-830-1680",
    href: null,
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/>
      </svg>
    ),
  },
  {
    label: "Email",
    value: "support@PaladinBusinessServices.net",
    href: "mailto:support@PaladinBusinessServices.net",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
      </svg>
    ),
  },
  {
    label: "Instagram",
    value: "@paladin_professional_insurance",
    href: "https://www.instagram.com/paladin_professional_insurance/",
    external: true,
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none"/>
      </svg>
    ),
  },
  {
    label: "Facebook",
    value: "Paladin Professional Insurance Solutions",
    href: "https://www.facebook.com/people/Paladin-Professional-Insurance-Solutions/61573336433957/",
    external: true,
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
      </svg>
    ),
  },
];

// ─────────────────────────────────────────────
// Contact Page
// ─────────────────────────────────────────────
export default function ContactPage() {
  usePageTitle("Contact Us");
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [payHovered, setPayHovered] = useState(null);
  const [activeAction, setActiveAction] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Submission failed.");
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "w-full font-constantia text-black-deep bg-white-pure border rounded-lg px-4 py-3 text-sm leading-relaxed outline-none transition-all duration-200 placeholder-black-deep placeholder-opacity-30 focus:border-blue-primary focus:shadow-[0_0_0_3px_rgba(0,45,181,0.08)]";

  return (
    <div className="w-full">
    {activeAction && (
      <QuickActionModal action={activeAction} onClose={() => setActiveAction(null)} />
    )}
    <main className="bg-white-pure">

      {/* ══════════════════════════════════════
          1. CONTACT FORM SECTION
      ══════════════════════════════════════ */}
      <section className="w-full bg-white-warm py-24 px-6 md:px-16 relative overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              "repeating-linear-gradient(90deg, rgba(1,46,114,0.03) 0px, rgba(1,46,114,0.03) 1px, transparent 1px, transparent 80px)",
          }}
        />

        <div className="max-w-5xl mx-auto relative grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">

          {/* LEFT: Hero text */}
          <div className="flex flex-col gap-6 lg:pt-4">
            <div className="flex items-center gap-3">
              <div className="w-1 h-4 bg-blue-dark rounded-full opacity-60" />
              <span className="font-cinzel text-blue-dark uppercase tracking-widest" style={{ fontSize: "11px", letterSpacing: "0.2em" }}>
                Get In Touch
              </span>
            </div>

            <h1
              className="font-cinzel font-bold text-blue-dark uppercase"
              style={{ fontSize: "clamp(28px, 4.5vw, 50px)", letterSpacing: "0.04em", lineHeight: "1.1" }}
            >
              Contact Us
            </h1>

            <div className="flex items-center gap-3">
              <div className="h-px w-12 bg-blue-dark opacity-20" />
              <div className="w-1.5 h-1.5 rounded-full bg-blue-dark opacity-30" />
            </div>

            <p className="font-constantia text-black-deep leading-relaxed" style={{ fontSize: "16px", opacity: 0.65, maxWidth: "380px" }}>
              Have questions about a policy, need a quote, or want to say hello? Fill out the form below or reach out to us directly.
            </p>

            {/* Quick contact links */}
            <div className="flex flex-col gap-3 mt-2">
              {CONTACT_ROWS.slice(0, 3).map(({ label, value, href, icon, external }) => (
                <a
                  key={label}
                  href={href || undefined}
                  target={external ? "_blank" : undefined}
                  rel={external ? "noopener noreferrer" : undefined}
                  className="flex items-start gap-3 group no-underline"
                  style={{ pointerEvents: href ? "auto" : "none" }}
                >
                  <div
                    className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-blue-dark transition-colors duration-200 group-hover:bg-blue-dark group-hover:text-white-pure"
                    style={{ backgroundColor: "rgba(1,46,114,0.08)" }}
                  >
                    {icon}
                  </div>
                  <span
                    className="font-constantia text-black-deep leading-relaxed pt-1 transition-colors duration-200 group-hover:text-blue-dark"
                    style={{ fontSize: "14px", opacity: 0.7 }}
                  >
                    {value}
                  </span>
                </a>
              ))}
            </div>
          </div>

          {/* RIGHT: Form card */}
          <div
            className="bg-white-pure rounded-2xl p-8 md:p-10"
            style={{ border: "1.5px solid rgba(1,46,114,0.1)", boxShadow: "0 4px 40px rgba(1,46,114,0.07)" }}
          >
            <div className="mb-8">
              <div className="w-full h-1 bg-blue-dark rounded-full mb-6" />
              <h2 className="font-cinzel font-bold text-blue-dark uppercase" style={{ fontSize: "15px", letterSpacing: "0.12em" }}>
                Send us a Message
              </h2>
            </div>

            {submitted ? (
              <div className="flex flex-col items-center text-center gap-4 py-10">
                <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ backgroundColor: "rgba(1,46,114,0.08)" }}>
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="rgba(1,46,114,0.8)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <h3 className="font-cinzel font-bold text-blue-dark uppercase" style={{ fontSize: "14px", letterSpacing: "0.1em" }}>
                  Message Sent!
                </h3>
                <p className="font-constantia text-black-deep" style={{ fontSize: "15px", opacity: 0.6 }}>
                  Thank you for reaching out. Our team will get back to you within 1–2 business days.
                </p>
                <button
                  onClick={() => { setSubmitted(false); setSubmitError(null); setForm({ name: "", email: "", subject: "", message: "" }); }}
                  className="font-cinzel uppercase tracking-widest text-blue-dark border border-blue-dark border-opacity-30 px-6 py-2 rounded-full hover:bg-blue-dark hover:text-white-pure transition-colors duration-200 mt-2"
                  style={{ fontSize: "11px" }}
                >
                  Send Another
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                <div>
                  <label className="font-cinzel text-blue-dark uppercase tracking-widest block mb-2" style={{ fontSize: "10px", letterSpacing: "0.2em" }}>
                    Full Name <span className="text-blue-primary">*</span>
                  </label>
                  <input
                    type="text" name="name" required placeholder="John Doe"
                    value={form.name} onChange={handleChange}
                    className={inputClass}
                    style={{ borderColor: "rgba(1,46,114,0.18)", fontSize: "15px" }}
                  />
                </div>

                <div>
                  <label className="font-cinzel text-blue-dark uppercase tracking-widest block mb-2" style={{ fontSize: "10px", letterSpacing: "0.2em" }}>
                    Email Address <span className="text-blue-primary">*</span>
                  </label>
                  <input
                    type="email" name="email" required placeholder="john@example.com"
                    value={form.email} onChange={handleChange}
                    className={inputClass}
                    style={{ borderColor: "rgba(1,46,114,0.18)", fontSize: "15px" }}
                  />
                </div>

                <div>
                  <label className="font-cinzel text-blue-dark uppercase tracking-widest block mb-2" style={{ fontSize: "10px", letterSpacing: "0.2em" }}>
                    Subject <span className="text-blue-primary">*</span>
                  </label>
                  <input
                    type="text" name="subject" required placeholder="How can we help you?"
                    value={form.subject} onChange={handleChange}
                    className={inputClass}
                    style={{ borderColor: "rgba(1,46,114,0.18)", fontSize: "15px" }}
                  />
                  <p className="font-constantia mt-1.5" style={{ fontSize: "12px", color: "rgba(1,7,4,0.38)" }}>
                    Enter a short subject so we can route your message to the right team.
                  </p>
                </div>

                <div>
                  <label className="font-cinzel text-blue-dark uppercase tracking-widest block mb-2" style={{ fontSize: "10px", letterSpacing: "0.2em" }}>
                    Message <span className="text-blue-primary">*</span>
                  </label>
                  <textarea
                    name="message" required rows={5} placeholder="Type your message here..."
                    value={form.message} onChange={handleChange}
                    className={`${inputClass} resize-none`}
                    style={{ borderColor: "rgba(1,46,114,0.18)", fontSize: "15px" }}
                  />
                  <p className="font-constantia mt-1.5 text-right" style={{ fontSize: "12px", color: "rgba(1,7,4,0.35)" }}>
                    {form.message.length} characters
                  </p>
                </div>

                {submitError && (
                  <div className="px-4 py-3 rounded-lg bg-red-50 border border-red-200">
                    <p className="font-constantia text-red-700 text-sm">{submitError}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-blue-dark text-white-pure font-cinzel uppercase tracking-widest py-4 rounded-lg hover:bg-blue-primary transition-colors duration-300 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{ fontSize: "12px", letterSpacing: "0.18em" }}
                >
                  {submitting ? "Sending..." : "Send Message"}
                  {!submitting && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
                    </svg>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          2. QUICK ACTIONS SECTION
      ══════════════════════════════════════ */}
      <section className="w-full bg-white-pure py-24 px-6 md:px-16 relative overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: "radial-gradient(circle at 1px 1px, rgba(1,46,114,0.04) 1px, transparent 0)",
            backgroundSize: "32px 32px",
          }}
        />

        <div className="max-w-6xl mx-auto relative">

          {/* Header */}
          <div className="flex flex-col items-center text-center gap-4 mb-16">
            <div className="flex items-center gap-3">
              <div className="w-1 h-4 bg-blue-dark rounded-full opacity-60" />
              <span className="font-cinzel text-blue-dark uppercase tracking-widest" style={{ fontSize: "11px", letterSpacing: "0.2em" }}>
                Quick Actions
              </span>
              <div className="w-1 h-4 bg-blue-dark rounded-full opacity-60" />
            </div>

            <h2
              className="font-cinzel font-bold text-blue-dark uppercase"
              style={{ fontSize: "clamp(22px, 3.5vw, 38px)", letterSpacing: "0.06em", lineHeight: "1.15" }}
            >
              How Can We Help You Today?
            </h2>

            <div className="flex items-center gap-3">
              <div className="h-px w-12 bg-blue-dark opacity-20" />
              <div className="w-1.5 h-1.5 rounded-full bg-blue-dark opacity-30" />
              <div className="h-px w-12 bg-blue-dark opacity-20" />
            </div>

            <p className="font-constantia text-black-deep max-w-2xl leading-relaxed" style={{ fontSize: "16px", opacity: 0.6 }}>
              Complete the applicable form below and submit it to our office via email, fax, or in person. Our team will review your request and respond within 1–2 business days. Fields marked with an asterisk (*) are required.
            </p>
          </div>

          {/* Action cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {ACTIONS.map((action) => (
              <div
                key={action.number}
                onClick={() => setActiveAction(action)}
                className="group bg-white-warm rounded-2xl p-7 flex flex-col gap-4 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 cursor-pointer"
                style={{ border: "1.5px solid rgba(1,46,114,0.1)" }}
              >
                <div className="flex items-center justify-between">
                  <span className="font-cinzel font-bold text-blue-dark" style={{ fontSize: "clamp(22px, 3vw, 28px)", opacity: 0.12, lineHeight: 1 }}>
                    {action.number}
                  </span>
                  <span
                    className="font-cinzel uppercase tracking-widest text-blue-primary border border-blue-primary border-opacity-30 px-3 py-1 rounded-full"
                    style={{ fontSize: "9px", letterSpacing: "0.18em" }}
                  >
                    {action.type}
                  </span>
                </div>

                <h3
                  className="font-cinzel font-bold text-blue-dark uppercase leading-snug group-hover:text-blue-primary transition-colors duration-300"
                  style={{ fontSize: "13px", letterSpacing: "0.06em" }}
                >
                  {action.title}
                </h3>

                <div className="h-px bg-blue-dark opacity-10" />

                <p className="font-constantia text-black-deep leading-relaxed flex-1" style={{ fontSize: "14px", opacity: 0.6 }}>
                  {action.description}
                </p>

                <div className="flex items-center gap-2 mt-auto pt-2">
                  <span
                    className="font-cinzel uppercase tracking-widest text-blue-dark group-hover:text-blue-primary transition-colors duration-300"
                    style={{ fontSize: "10px", letterSpacing: "0.15em" }}
                  >
                    Request
                  </span>
                  <span className="text-blue-dark group-hover:text-blue-primary group-hover:translate-x-1 transition-all duration-300" style={{ fontSize: "14px" }}>
                    →
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          3. CONTACT & LOCATION SECTION
      ══════════════════════════════════════ */}
      <section className="w-full py-20 px-6 md:px-16 relative overflow-hidden" style={{ backgroundColor: "#012E72" }}>
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              "repeating-linear-gradient(90deg, rgba(255,255,255,0.03) 0px, rgba(255,255,255,0.03) 1px, transparent 1px, transparent 80px)",
          }}
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse 70% 60% at 50% 40%, rgba(0,45,181,0.45) 0%, transparent 100%)" }}
        />

        <div className="max-w-5xl mx-auto relative">

          {/* Header */}
          <div className="flex flex-col items-center text-center gap-4 mb-14">
            <div className="flex items-center gap-3">
              <div className="w-1 h-4 rounded-full" style={{ backgroundColor: "rgba(255,255,255,0.4)" }} />
              <span className="font-cinzel text-white-pure uppercase tracking-widest" style={{ fontSize: "11px", letterSpacing: "0.2em", opacity: 0.6 }}>
                Contact &amp; Location
              </span>
              <div className="w-1 h-4 rounded-full" style={{ backgroundColor: "rgba(255,255,255,0.4)" }} />
            </div>

            <h2
              className="font-cinzel font-bold text-white-pure uppercase"
              style={{ fontSize: "clamp(22px, 3.5vw, 38px)", letterSpacing: "0.06em", lineHeight: "1.15" }}
            >
              We'd Love to Hear From You
            </h2>

            <p className="font-constantia text-white-pure max-w-lg leading-relaxed" style={{ fontSize: "16px", opacity: 0.6 }}>
              Reach out to us through any of the channels below — our licensed agents are ready to assist.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

            {/* Contact rows */}
            <div
              className="rounded-2xl p-8 flex flex-col gap-1"
              style={{ backgroundColor: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)" }}
            >
              {/* Quick Payments */}
              <div className="mb-6">
                <p className="font-cinzel text-white-pure uppercase tracking-widest mb-3" style={{ fontSize: "10px", opacity: 0.5 }}>
                  Quick Payments
                </p>
                <p className="font-constantia text-white-pure italic mb-4" style={{ fontSize: "14px", opacity: 0.55 }}>
                  Securely manage your policies and bills right from our portal.
                </p>
                <div className="flex flex-col gap-2">
                  {["e-Pay Now", "Online Bill Pay"].map((label) => (
                    <button
                      key={label}
                      onMouseEnter={() => setPayHovered(label)}
                      onMouseLeave={() => setPayHovered(null)}
                      className="w-full font-cinzel font-bold rounded-full py-2.5 px-6 transition-all duration-300 flex items-center justify-center gap-2"
                      style={{
                        fontSize: "0.68rem",
                        letterSpacing: "0.15em",
                        textTransform: "uppercase",
                        backgroundColor: payHovered === label ? "#FFFFFF" : "transparent",
                        color: payHovered === label ? "#012E72" : "#FFFFFF",
                        border: "1.5px solid rgba(255,255,255,0.35)",
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="h-px mb-4" style={{ backgroundColor: "rgba(255,255,255,0.1)" }} />

              {/* Contact rows */}
              {CONTACT_ROWS.map(({ label, value, href, external, icon }) => {
                const inner = (
                  <div className="flex items-center gap-4 py-3 group">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 text-white-pure" style={{ backgroundColor: "rgba(255,255,255,0.1)" }}>
                      {icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-cinzel uppercase" style={{ fontSize: "9px", letterSpacing: "0.2em", color: "rgba(255,255,255,0.4)" }}>
                        {label}
                      </p>
                      <p className="font-constantia text-white-pure truncate" style={{ fontSize: "14px", opacity: 0.75 }}>
                        {value}
                      </p>
                    </div>
                  </div>
                );
                return href ? (
                  <a key={label} href={href} target={external ? "_blank" : undefined} rel={external ? "noopener noreferrer" : undefined} className="no-underline block hover:opacity-100 opacity-90 transition-opacity">
                    {inner}
                  </a>
                ) : (
                  <div key={label}>{inner}</div>
                );
              })}
            </div>

            {/* Address card */}
            <div className="flex flex-col gap-6">
              <div
                className="rounded-2xl p-8 flex flex-col gap-4"
                style={{ backgroundColor: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)" }}
              >
                <p className="font-cinzel text-white-pure uppercase tracking-widest" style={{ fontSize: "10px", opacity: 0.5 }}>
                  Office Address
                </p>
                <p className="font-constantia text-white-pure leading-relaxed" style={{ fontSize: "16px", opacity: 0.8 }}>
                  3787 Transport ST Suite A7 Box #5<br />
                  Ventura, CA 93003
                </p>
                <div className="h-px" style={{ backgroundColor: "rgba(255,255,255,0.1)" }} />
                <p className="font-cinzel text-white-pure uppercase tracking-widest" style={{ fontSize: "10px", opacity: 0.5 }}>
                  Office Hours
                </p>
                {[
                  { day: "Mon – Fri", hours: "9:00 AM – 5:00 PM" },
                  { day: "Saturday", hours: "By Appointment" },
                  { day: "Sunday", hours: "Closed" },
                ].map(({ day, hours }) => (
                  <div key={day} className="flex justify-between items-center">
                    <span className="font-constantia text-white-pure" style={{ fontSize: "14px", opacity: 0.5 }}>{day}</span>
                    <span className="font-constantia text-white-pure" style={{ fontSize: "14px", opacity: 0.8 }}>{hours}</span>
                  </div>
                ))}
              </div>

              <div
                className="rounded-2xl p-6 flex flex-col gap-2"
                style={{ backgroundColor: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)" }}
              >
                <p className="font-constantia text-white-pure italic" style={{ fontSize: "13px", opacity: 0.45 }}>
                  Licensed in CA, AZ, ID, IL, IN, NV, NC, OH and TX
                </p>
                <p className="font-cinzel text-white-pure uppercase" style={{ fontSize: "9px", letterSpacing: "0.15em", opacity: 0.35 }}>
                  Paladin Professional Insurance Solutions · Protecting What Matters, With Care That Shows
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          4. FIND US (MAP) SECTION
      ══════════════════════════════════════ */}
      <FindUsSection />

    </main>
    <Footer />
    </div>
  );
}
